import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import TableView from "../../../../components/tableView";
import { SearchIcon } from "../../../../components/ui/icons";
import {
  DataAvailabilityMessages,
  TABLE_RENDERING_MODES,
} from "../../../../common/constants";
import { useSpinner } from "../../../../customHooks";

import {
  fetchSuperAdminDeviceData,
  selector as superAdminDeviceApi,
} from "./superAdminDeviceSlice";

import SuperAdminDeviceColumns from "./superAdminDeviceColumns";
import PendingDeletionsModal from "../../../../components/pendingDeletionsModal";

import "../../users.scss";
import "../superadminusers.scss";
import "./deviceManagement.scss";

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

const SuperAdminDeviceManagement = () => {
  const dispatch: ThunkDispatch<any, undefined, Action> = useDispatch();

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const superAdminDeviceData = useSelector(superAdminDeviceApi);

  useSpinner(superAdminDeviceData.status);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearchTerm]);

  const fetchDevices = useCallback(() => {
    dispatch(
      fetchSuperAdminDeviceData({
        page: pageIndex + 1,
        limit: pageSize,
        filter: debouncedSearchTerm,
      })
    );
  }, [dispatch, pageIndex, pageSize, debouncedSearchTerm]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const tableData = useMemo(() => {
    const devices = superAdminDeviceData?.devices?.values || [];

    return devices.map((device: any) => ({
      ...device,
      deviceDisplayName: device?.displayName || "",
      deviceId: device?.deviceId || "",
    }));
  }, [superAdminDeviceData?.devices]);

  const totalCount = superAdminDeviceData?.devices?.totalCount ?? 0;

  const columns = SuperAdminDeviceColumns({
    onDeleteSuccess: fetchDevices,
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  };

  const handlePaginationChange = useCallback(
    (newPageIndex: number, newPageSize: number) => {
      setPageIndex(newPageIndex);
      setPageSize(newPageSize);
    },
    []
  );

  return (
    <div className="admin-users-container superadmin_users_container superadmin-device-management-container">
      <Col>
        <div className="dm-heading-container">
          <Row>
            <Col sm={4}>
              <div className="heading">Device Management</div>
            </Col>

            <Col sm={8}>
              <div className="superadmin-device-header-actions">
                <div className="superadmin-pending-deletions-btn">
                  <PendingDeletionsModal />
                </div>

                <div className="superadmin-device-search-container">
                  <input
                    type="text"
                    className="superadmin-device-search-input"
                    placeholder="Search for devices..."
                    aria-label="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <span className="superadmin-device-search-icon">
                    <SearchIcon />
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Col>

      <Col>
        <div className="dm-body">
          <Row>
            <Col sm={12}>
              <div className="dm-table-container-organizations">
                <TableView
                  data={tableData}
                  columns={columns}
                  mode={TABLE_RENDERING_MODES.SERVER_SIDE}
                  totalCount={totalCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPaginationChange={handlePaginationChange}
                  noDataMessage={DataAvailabilityMessages.NO_DEVICE_AVAILABLE}
                />
              </div>
            </Col>
          </Row>
        </div>
      </Col>
    </div>
  );
};

export default SuperAdminDeviceManagement;